import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMetaTags } from '../../hooks/useMetaTags';
import ExternalLink from '../common/ExternalLink';

/**
 * TermsOfService component displays the application's terms of service.
 */
function TermsOfService() {
  const { t, i18n } = useTranslation('terms');
  const { t: commonT } = useTranslation();
  const [readingTime, setReadingTime] = useState(7); // Default estimated reading time in minutes
  
  // Set the meta tags for this page
  useMetaTags(
    t('metaTitle', 'Terms of Service - TapDonkey'),
    t('metaDescription', 'Terms of Service for TapDonkey, outlining the rules and regulations for using our service.')
  );

  // Date formatting based on user locale
  const formatDate = (date: Date) => {
    try {
      return date.toLocaleDateString(i18n.language || navigator.language || 'en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
  };

  // Generate anchor links for table of contents
  const generateAnchorId = (sectionNumber: number) => `terms-section-${sectionNumber}`;

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <h1 className="mb-4">{t('title', 'Terms of Service')}</h1>
          
          <div className="card shadow-sm rounded-4 mb-4">
            <div className="card-body p-4 p-md-5">
              <div className="d-flex justify-content-between align-items-center mb-4">
                                <p className="text-body-secondary small mb-0">                  {commonT('common.lastUpdated', 'Last updated')}: {formatDate(new Date('2025-05-15'))}                </p>
                <p className="text-body-secondary small mb-0">
                  <i className="bi bi-clock me-1"></i>
                  {commonT('common.readingTime', 'Reading time')}: ~{readingTime} {commonT('common.minutes', 'minutes')}
                </p>
              </div>
              
              <div className="mb-4 p-3 bg-body-tertiary border rounded-3">
                <h2 className="h6 fw-bold mb-3">{commonT('common.tableOfContents', 'Table of Contents')}</h2>
                <ol className="list-unstyled mb-0">
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(1)}`} className="text-decoration-none link-body-emphasis">
                      1. {t('introduction.title', 'Introduction')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(2)}`} className="text-decoration-none link-body-emphasis">
                      2. {t('communications.title', 'Communications')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(3)}`} className="text-decoration-none link-body-emphasis">
                      3. {t('purchases.title', 'Purchases')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(4)}`} className="text-decoration-none link-body-emphasis">
                      4. {t('content.title', 'Content')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(5)}`} className="text-decoration-none link-body-emphasis">
                      5. {t('prohibited.title', 'Prohibited Uses')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(6)}`} className="text-decoration-none link-body-emphasis">
                      6. {t('intellectual.title', 'Intellectual Property')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(7)}`} className="text-decoration-none link-body-emphasis">
                      7. {t('termination.title', 'Termination')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(8)}`} className="text-decoration-none link-body-emphasis">
                      8. {t('disclaimer.title', 'Disclaimer')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(9)}`} className="text-decoration-none link-body-emphasis">
                      9. {t('limitation.title', 'Limitation of Liability')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(10)}`} className="text-decoration-none link-body-emphasis">
                      10. {t('indemnification.title', 'Indemnification')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(11)}`} className="text-decoration-none link-body-emphasis">
                      11. {t('governing.title', 'Governing Law')}
                    </a>
                  </li>
                  <li className="mb-0">
                    <a href={`#${generateAnchorId(12)}`} className="text-decoration-none link-body-emphasis">
                      12. {t('changes.title', 'Changes to Terms')}
                    </a>
                  </li>
                </ol>
              </div>

                            <div className="alert alert-info mb-4">                <p className="small mb-0">                  <strong>{commonT('common.versionHistory', 'Version History')}:</strong> {t('versionInfo', 'This is version 1.1 of our Terms of Service, effective from {{date}}', { date: formatDate(new Date('2025-05-15')) })}                </p>              </div>

              <section id={generateAnchorId(1)} className="mb-4">
                <h2 className="h5 fw-bold">1. {t('introduction.title', 'Introduction')}</h2>
                <p>
                  {t('introduction.p1', 'Welcome to TapDonkey. These Terms of Service govern your use of our web application located at')} <ExternalLink href="https://tapdonkey.com">https://tapdonkey.com</ExternalLink>.
                </p>
                <p>
                  {t('introduction.p2', 'By accessing or using our service, you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the service.')}
                </p>
                <p>
                  {t('introduction.p3', 'Please also review our')} <Link to="/privacy-policy">{t('introduction.privacyLink', 'Privacy Policy')}</Link> {t('introduction.p4', 'to understand our practices regarding your data.')}
                </p>
              </section>

              <section id={generateAnchorId(2)} className="mb-4">
                <h2 className="h5 fw-bold">2. {t('communications.title', 'Communications')}</h2>
                <p>
                  {t('communications.p1', 'By using our service, you may optionally provide your email address to receive a link to your counter. This is solely for providing access to your counter and to ensure you do not lose access.')}
                </p>
                <p>
                  {t('communications.p2', 'Your email address is not stored on our servers and is only used for the one-time delivery of the counter link. You can choose not to provide an email if you prefer.')}
                </p>
              </section>

              <section id={generateAnchorId(3)} className="mb-4">
                <h2 className="h5 fw-bold">3. {t('purchases.title', 'Purchases')}</h2>
                <p>
                  {t('purchases.p1', 'TapDonkey currently offers all its services free of charge. If we introduce paid services in the future, additional terms will be provided.')}
                </p>
              </section>

              <section id={generateAnchorId(4)} className="mb-4">
                <h2 className="h5 fw-bold">4. {t('content.title', 'Content')}</h2>
                <p>
                  {t('content.p1', 'When you create counters and custom button names, you are entirely responsible for the content you create. Content that is harmful, offensive, or illegal is strictly prohibited.')}
                </p>
                <p>
                  {t('content.p2', 'TapDonkey reserves the right to remove any counters or content that violates these terms or is otherwise objectionable.')}
                </p>
              </section>

              <section id={generateAnchorId(5)} className="mb-4">
                <h2 className="h5 fw-bold">5. {t('prohibited.title', 'Prohibited Uses')}</h2>
                <p>
                  {t('prohibited.p1', 'You may use TapDonkey only for lawful purposes and in accordance with these Terms of Service. You agree not to use TapDonkey:')}
                </p>
                <ul>
                  <li>{t('prohibited.item1', 'In any way that violates any applicable national or international law or regulation.')}</li>
                  <li>{t('prohibited.item2', 'To transmit, or procure the sending of, any advertising or promotional material without our prior consent.')}</li>
                  <li>{t('prohibited.item3', 'To impersonate or attempt to impersonate TapDonkey, a TapDonkey employee, another user, or any other person or entity.')}</li>
                  <li>{t('prohibited.item4', 'To engage in any other conduct that restricts or inhibits anyone\'s use or enjoyment of the service, or which may harm TapDonkey or users of the service, or expose them to liability.')}</li>
                </ul>
              </section>

              <section id={generateAnchorId(6)} className="mb-4">
                <h2 className="h5 fw-bold">6. {t('intellectual.title', 'Intellectual Property')}</h2>
                <p>
                  {t('intellectual.p1', 'The service and its original content, features, and functionality are and will remain the exclusive property of TapDonkey and its licensors. The service is protected by copyright, trademark, and other laws of both the European Union and foreign countries.')}
                </p>
                <p>
                  {t('intellectual.p2', 'Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of TapDonkey.')}
                </p>
              </section>

              <section id={generateAnchorId(7)} className="mb-4">
                <h2 className="h5 fw-bold">7. {t('termination.title', 'Termination')}</h2>
                <p>
                  {t('termination.p1', 'We may terminate or suspend access to our service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.')}
                </p>
                <p>
                  {t('termination.p2', 'All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.')}
                </p>
              </section>

              <section id={generateAnchorId(8)} className="mb-4">
                <h2 className="h5 fw-bold">8. {t('disclaimer.title', 'Disclaimer')}</h2>
                <p>
                  {t('disclaimer.p1', 'Your use of the service is at your sole risk. The service is provided on an "AS IS" and "AS AVAILABLE" basis. The service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.')}
                </p>
                <p>
                  {t('disclaimer.p2', 'TapDonkey does not warrant that a) the service will function uninterrupted, secure, or available at any particular time or location; b) any errors or defects will be corrected; c) the service is free of viruses or other harmful components; or d) the results of using the service will meet your requirements.')}
                </p>
              </section>

              <section id={generateAnchorId(9)} className="mb-4">
                <h2 className="h5 fw-bold">9. {t('limitation.title', 'Limitation of Liability')}</h2>
                <p>
                  {t('limitation.p1', 'In no event shall TapDonkey, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:')}
                </p>
                <ul>
                  <li>{t('limitation.item1', 'Your access to or use of or inability to access or use the service;')}</li>
                  <li>{t('limitation.item2', 'Any conduct or content of any third party on the service;')}</li>
                  <li>{t('limitation.item3', 'Any content obtained from the service; and')}</li>
                  <li>{t('limitation.item4', 'Unauthorized access, use, or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence), or any other legal theory, whether or not we have been informed of the possibility of such damage.')}</li>
                </ul>
              </section>

              <section id={generateAnchorId(10)} className="mb-4">
                <h2 className="h5 fw-bold">10. {t('indemnification.title', 'Indemnification')}</h2>
                <p>
                  {t('indemnification.p1', 'You agree to defend, indemnify, and hold harmless TapDonkey and its licensors, service providers, employees, agents, officers, directors, and contractors from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys\' fees) arising out of or relating to your violation of these Terms of Service or your use of the service.')}
                </p>
              </section>

              <section id={generateAnchorId(11)} className="mb-4">
                <h2 className="h5 fw-bold">11. {t('governing.title', 'Governing Law')}</h2>
                <p>
                  {t('governing.p1', 'These Terms shall be governed and construed in accordance with the laws of the European Union, without regard to its conflict of law provisions.')}
                </p>
                <p>
                  {t('governing.p2', 'Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.')}
                </p>
              </section>

              <section id={generateAnchorId(12)} className="mb-4">
                <h2 className="h5 fw-bold">12. {t('changes.title', 'Changes to Terms')}</h2>
                <p>
                  {t('changes.p1', 'We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days\' notice prior to any new terms taking effect.')}
                </p>
                <p>
                  {t('changes.p2', 'By continuing to access or use our service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the service.')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService; 